// src/components/IconCluesManager.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getAllIconClues, updateIconClueWords, mergeLegacyEmojiClues, clearIconCluesCache } from '../services/iconCluesService';
import { getIconManifest, getIconUrl } from '../services/iconService';
import materialSymbolNames from '../material_symbols.json';
import '../styles/icon-clues.css';
import UiIcon from './ui/UiIcon';

function seededShuffle(items, seed) {
  const out = [...items];
  let a = seed;
  const rand = () => { // mulberry32
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

const PAGE_SIZE = 50;

function IconPreview({ type, name, value, customUrls }) {
  if (type === 'custom') {
    const url = customUrls[name];
    return url ? (
      <img src={url} alt={name} className="ic-icon-img" />
    ) : (
      <span className="ic-icon-placeholder">...</span>
    );
  }
  if (type === 'symbol') {
    return <span className="material-symbols-outlined ic-symbol">{name}</span>;
  }
  return <span className="ic-emoji">{value}</span>;
}

// Small inline word input — Enter adds a word, Backspace on empty removes last
function WordInput({ icon, words, onSave }) {
  const [inputVal, setInputVal] = useState('');
  const inputRef = useRef();

  function addWord() {
    const w = inputVal.trim().toUpperCase();
    setInputVal('');
    inputRef.current?.focus();
    if (w && !words.includes(w)) {
      onSave({ add: [w] });
    }
  }

  function removeWord(word) {
    onSave({ remove: [word] });
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addWord();
    }
  }

  return (
    <div className="ic-word-section">
      {words.length > 0 && (
        <div className="ic-word-tags">
          {words.map(w => (
            <span key={w} className="ic-tag ic-tag--editable">
              {w}
              <button
                className="ic-tag-remove"
                onClick={() => removeWord(w)}
                type="button"
                aria-label={`Remove ${w}`}
              ><UiIcon name="close" /></button>
            </span>
          ))}
        </div>
      )}
      <input
        ref={inputRef}
        type="text"
        className="ic-word-input"
        value={inputVal}
        onChange={e => setInputVal(e.target.value.toUpperCase())}
        onKeyDown={handleKeyDown}
        placeholder="Add word..."
      />
    </div>
  );
}

function IconCluesManager() {
  const [search, setSearch] = useState('');
  const [iconClues, setIconClues] = useState({});
  const [baseIcons, setBaseIcons] = useState([]);
  const [customUrls, setCustomUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [showSymbols, setShowSymbols] = useState(true);
  const [showEmoji, setShowEmoji] = useState(true);
  const [showCustom, setShowCustom] = useState(false);
  const [shuffleSeed, setShuffleSeed] = useState(null); // null = alphabetical order

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const toMap = (list) => {
        const map = {};
        for (const ic of list) {
          map[ic.id] = ic;
          // Also index by value for emoji/custom where id may differ from generated icon.id
          if (ic.value) map[`val:${ic.value}`] = ic;
        }
        return map;
      };
      const cluesList = await getAllIconClues();
      setIconClues(toMap(cluesList));
      // Fold legacy label-keyed emoji docs into the character-keyed ones in the background
      mergeLegacyEmojiClues(cluesList)
        .then(merged => { if (merged !== cluesList) setIconClues(toMap(merged)); })
        .catch(err => console.error('Failed to merge legacy emoji clues:', err));

      const symbols = materialSymbolNames.map(name => ({
        type: 'symbol',
        name,
        value: `{${name}}`,
        id: `symbol:${name}`
      }));

      let emojis = [];
      try {
        const res = await fetch('https://unpkg.com/emojibase-data@latest/en/compact.json');
        const data = await res.json();
        emojis = data.map(item => ({
          type: 'emoji',
          name: item.label,
          value: item.unicode,
          id: `emoji:${item.unicode}`
        }));
      } catch (err) {
        console.error('Failed to load emojis:', err);
      }

      const manifest = await getIconManifest();
      const customIcons = Object.keys(manifest).map(name => ({
        type: 'custom',
        name,
        value: `{c.${name}}`,
        id: `custom:${name}`
      }));

      setBaseIcons([...customIcons, ...symbols, ...emojis]);
    } catch (err) {
      console.error('Failed to load icon clues data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showCustom) return;
    const customIcons = baseIcons.filter(ic => ic.type === 'custom' && !customUrls[ic.name]);
    if (customIcons.length === 0) return;

    customIcons.forEach(async (icon) => {
      try {
        const url = await getIconUrl(icon.name);
        setCustomUrls(prev => ({ ...prev, [icon.name]: url }));
      } catch { /* skip */ }
    });
  }, [showCustom, baseIcons]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(0);
  }, [search, showSymbols, showEmoji, showCustom, shuffleSeed]);

  // Applies one add/remove to the local list right away (from the latest state, so quick
  // consecutive edits to one icon don't clobber each other) and persists just that change.
  async function handleSave(icon, { add = [], remove = [] }) {
    setIconClues(prev => {
      const current = (prev[icon.id] ?? prev[`val:${icon.value}`])?.words || [];
      const words = [...current.filter(w => !remove.includes(w)), ...add.filter(w => !current.includes(w))];
      const entry = { id: icon.id, type: icon.type, name: icon.name, value: icon.value, words };
      return { ...prev, [icon.id]: entry, [`val:${icon.value}`]: entry };
    });
    try {
      // Emoji docs are keyed by the character, like puzzle sync does
      await updateIconClueWords(icon.type, icon.type === 'emoji' ? icon.value : icon.name, icon.value, { add, remove });
      clearIconCluesCache();
    } catch (err) {
      console.error('Failed to save icon clue:', err);
      alert('Failed to save: ' + err.message);
      loadData();
    }
  }

  const matchingIcons = baseIcons.filter(ic => {
    if (ic.type === 'symbol' && !showSymbols) return false;
    if (ic.type === 'emoji' && !showEmoji) return false;
    if (ic.type === 'custom' && !showCustom) return false;
    if (!search.trim()) return true;
    return ic.name.toLowerCase().includes(search.toLowerCase());
  });

  // Seeded so the order stays put across re-renders (edits, paging) until shuffled again
  const filteredIcons = useMemo(
    () => (shuffleSeed === null ? matchingIcons : seededShuffle(matchingIcons, shuffleSeed)),
    [matchingIcons.length, baseIcons, search, showSymbols, showEmoji, showCustom, shuffleSeed] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const totalPages = Math.ceil(filteredIcons.length / PAGE_SIZE);
  const pageIcons = filteredIcons.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) {
    return <div className="ic-loading">Loading icons...</div>;
  }

  return (
    <div className="ic-manager">
      <h1>Icon Clues</h1>
      <p className="ic-description">
        Tag icons with crossword answer words. When editing a clue, matching icons appear at the top of the suggestion dropdown.
        Associations are also built automatically when you save puzzles.
      </p>

      <div className="ic-toolbar">
        <div className="ic-search-bar">
          <input
            type="text"
            className="ic-search-input"
            placeholder="Search icons by name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="ic-clear-search" onClick={() => setSearch('')} aria-label="Clear search"><UiIcon name="close" /></button>
          )}
        </div>

        <div className="ic-filters">
          <label className="ic-filter-label">
            <input type="checkbox" checked={showSymbols} onChange={e => setShowSymbols(e.target.checked)} />
            Material Symbols ({materialSymbolNames.length.toLocaleString()})
          </label>
          <label className="ic-filter-label">
            <input type="checkbox" checked={showEmoji} onChange={e => setShowEmoji(e.target.checked)} />
            Emoji
          </label>
          <label className="ic-filter-label">
            <input type="checkbox" checked={showCustom} onChange={e => setShowCustom(e.target.checked)} />
            Custom Icons
          </label>
        </div>

        <button className="btn-secondary" onClick={() => setShuffleSeed(Math.floor(Math.random() * 2 ** 31))}>
          <UiIcon name="shuffle" /> Randomize
        </button>
        {shuffleSeed !== null && (
          <button className="btn-tertiary" onClick={() => setShuffleSeed(null)}>Reset order</button>
        )}
      </div>

      <div className="ic-results-info">
        {filteredIcons.length.toLocaleString()} icons
        {search && ` matching "${search}"`}
        {totalPages > 1 && ` — page ${page + 1} of ${totalPages}`}
      </div>

      {filteredIcons.length === 0 ? (
        <p className="ic-empty">No icons found{search ? ` for "${search}"` : ''}.</p>
      ) : (
        <>
          <div className="ic-grid">
            {pageIcons.map(icon => {
              const words = (iconClues[icon.id] ?? iconClues[`val:${icon.value}`])?.words || [];

              return (
                <div key={icon.id} className="ic-card">
                  <div className="ic-preview">
                    <IconPreview
                      type={icon.type}
                      name={icon.name}
                      value={icon.value}
                      customUrls={customUrls}
                    />
                  </div>
                  <div className="ic-name" title={icon.name}>{icon.name}</div>
                  <WordInput
                    icon={icon}
                    words={words}
                    onSave={(next) => handleSave(icon, next)}
                  />
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="ic-pagination">
              <button className="btn-secondary" onClick={() => setPage(0)} disabled={page === 0}>«</button>
              <button className="btn-secondary" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</button>
              <span className="ic-page-info">Page {page + 1} / {totalPages}</span>
              <button className="btn-secondary" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next ›</button>
              <button className="btn-secondary" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default IconCluesManager;
